<template>
    <div>
        <h2 class="title is-4">
            Transactions
        </h2>

        <h5 class="subtitle is-6">
            This table lists the financial transactions for your rental properties.
        </h5>

        <form
            class="mb-5"
            @submit.prevent="searchTransactions"
        >
            <nav class="level is-block-tablet-only is-block-desktop-only">
                <div class="level-left is-block-tablet-only is-block-desktop-only">
                </div>

                <div class="level-right is-block-tablet-only is-block-desktop-only">
                    <div class="level-item is-block-mobile is-block-tablet-only is-block-desktop-only">
                        <div class="field">
                            <label class="label is-size-7 has-text-weight-medium">
                                From date
                            </label>
                            <b-datepicker
                                v-model="fromDate"
                                placeholder="Search from..."
                                :min-date="minimumDate ? $dayjs(minimumDate).toDate() : null"
                                :max-date="toDate"
                                icon="calendar-alt"
                                locale="en-GB"
                                @input="searchTransactions"
                            />
                        </div>
                    </div>
                    <div class="level-item is-block-mobile is-block-tablet-only is-block-desktop-only">
                        <div class="field">
                            <label class="label is-size-7 has-text-weight-medium">
                                To date
                            </label>
                            <b-datepicker
                                v-model="toDate"
                                placeholder="Search to..."
                                :min-date="fromDate"
                                :max-date="$dayjs().toDate()"
                                icon="calendar-alt"
                                locale="en-GB"
                                @input="searchTransactions"
                            />
                        </div>
                    </div>
                    <div class="level-item is-block-mobile is-block-tablet-only is-block-desktop-only">
                        <div class="field">
                            <label class="label is-size-7 has-text-weight-medium">
                                Portfolio
                            </label>
                            <div class="control">
                                <OwnerGroupsSelect
                                    v-model="ownerGroup"
                                    @input="searchTransactions"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </nav>
        </form>

        <Notification v-if="! ownerGroup">
            <strong>Select a portfolio</strong> to view transactions.
        </Notification>

        <template v-else>
            <Notification v-if="minimumDate">
                Transactions will only be displayed from {{ $dayjs(minimumDate).format('MMMM YYYY') }} onwards.
            </Notification>

            <Notification v-if="transactions && ! hasTransactions">
                No transactions found.
            </Notification>

            <div
                v-else
                class="table-container"
            >
                <table class="table is-fullwidth is-bordered is-striped">
                    <thead
                        v-if="! transactions || hasTransactions"
                        class="is-hidden-mobile"
                    >
                        <tr>
                            <th>
                                <span class="is-hidden-touch">Transaction</span> Date
                            </th>
                            <th class="is-hidden-touch is-hidden-desktop-only">
                                Statement
                            </th>
                            <th class="is-hidden-touch">
                                Description
                            </th>
                            <th class="is-hidden-touch">
                                Property
                            </th>
                            <th class="is-hidden-touch">
                                Receipts
                            </th>
                            <th class="is-hidden-touch">
                                Payments
                            </th>
                            <th class="is-hidden-touch">
                                Running Balance
                            </th>
                            <th class="is-hidden-touch">
                                Attachment
                            </th>
                            <th class="is-hidden-desktop">
                                Amount
                            </th>
                        </tr>
                    </thead>
                    <tbody v-if="hasTransactions">
                        <TransactionRow
                            v-for="row in transactions"
                            :key="row.TransactionPK"
                            :row="row"
                        />
                    </tbody>
                    <SkeletonRow
                        v-if="! transactions && ! hasTransactions"
                        :row-count="5"
                        :desktop-count="8"
                        :tablet-count="7"
                        :mobile-count="2"
                    />
                </table>

                <b-tooltip
                    v-if="hasTransactions"
                    label="Download latest 1000 transactions as a CSV file"
                    position="is-right"
                >
                    <b-button
                        type="is-info"
                        tag="a"
                        :href="`/contacts/transactions/download?${queryParamsString}`"
                    >
                        Download report
                    </b-button>
                </b-tooltip>
            </div>
        </template>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import Notification from '../components/Notification.vue';
import TransactionRow from '../components/TransactionRow.vue';
import SkeletonRow from '../components/SkeletonRow';
import OwnerGroupsSelect from '../components/OwnerGroupsSelect';

export default {
    components: {
        SkeletonRow,
        Notification,
        TransactionRow,
        OwnerGroupsSelect,
    },

    data: function() {
        const { query } = this.$route;

        return {
            ownerGroup: query.ownerGroup || null,
            fromDate: query.fromDate ? this.$dayjs.unix(Number(query.fromDate)).toDate() : this.$dayjs().subtract(1, 'year').toDate(),
            toDate: query.toDate ? this.$dayjs.unix(Number(query.toDate)).toDate() : this.$dayjs().toDate(),
        };
    },

    computed: {
        ...mapState('transactions', [
            'transactions',
        ]),
        ...mapGetters('transactions', [
            'hasTransactions',
        ]),
        minimumDate() {
            return this.$meta.features.owners.transactions_from;
        },
        queryParams() {
            return {
                ...this.$data,
                // change format of dates to timestamp
                fromDate: this.$dayjs(this.fromDate).unix(),
                toDate: this.$dayjs(this.toDate).unix(),
            };
        },
        queryParamsString() {
            // remove empty and null values from object
            let params = _.omitBy(this.queryParams, (value) => (! value));
            params = new URLSearchParams(params);
            return params.toString();
        },
    },

    mounted() {
        this.getTransactions();
    },

    methods: {
        searchTransactions() {
            this.$router.push({
                query: this.queryParams,
            });

            this.getTransactions();
        },
        getTransactions() {
            if (this.ownerGroup) {
                this.$store.dispatch('transactions/getTransactions', this.$data);
            }
        },
    },
};
</script>
